'use client';

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslation, useUi } from '@weareconceptstudio/core';

//* Components
import Header from '@/components/Header';
import Preloader from '@/components/Preloader';
import HighOrderComponent from '@/components/HighOrderComponent';

//* Styles
import Variables from '@/styles/variables';
import Typography from '@/styles/typography';
import HelperClass from '@/styles/helperClass';

//* HOC's
import { withDataContext } from '@/context';

const RootTemplate = ({ children, preloader }) => {
	const { isBurgerMenuOpen, popupIsOpen, winWidth } = useUi();
	const pathname = usePathname();
	const { selectedLang } = useTranslation();

	//! State & Refs
	const [burgerHeight, setBurgerHeight] = useState(0);
	const burgerRef = useRef(null);

	//! Derived values
	const isMobileScreen = useMemo(() => winWidth < 768, [winWidth]);

	const cleanedPathname = useMemo(() => {
		return selectedLang ? pathname.replaceAll(`/${selectedLang}`, '') : pathname;
	}, [pathname, selectedLang]);

	const isPortfolioIndividual = cleanedPathname.startsWith('/portfolio/') && cleanedPathname !== '/portfolio/';

	const shouldShowPreloader = useMemo(() => {
		return ['/', '/portfolio/'].includes(cleanedPathname) || isPortfolioIndividual;
	}, [cleanedPathname, isPortfolioIndividual]);

	const isHomePortfolio = useMemo(() => {
		return ['/', '/portfolio/'].includes(cleanedPathname);
	}, [cleanedPathname]);

	const isPreloaderActive = preloader && shouldShowPreloader;

	const updateBurgerHeight = useCallback(() => {
		if (burgerRef.current) {
			setBurgerHeight(burgerRef.current.offsetHeight);
		}
	}, []);

	//! Effects
	useEffect(() => {
		updateBurgerHeight();
	}, [isBurgerMenuOpen, updateBurgerHeight]);

	return (
		<>
			<Variables />
			<HelperClass />
			<Typography
				$popupIsOpen={popupIsOpen}
				$burgerHeight={burgerHeight}
				$showPreloader={shouldShowPreloader}
				$isBurgerMenuOpen={isBurgerMenuOpen}
				$isPreloaderActive={isPreloaderActive}
			/>

			<Preloader
				isPreloaderActive={isPreloaderActive}
				isHomePortfolio={isHomePortfolio}
			/>

			<Header
				ref={burgerRef}
				cleanedPathname={cleanedPathname}
				showPreloader={shouldShowPreloader}
				isMobileScreen={isMobileScreen}
				isPreloaderActive={isPreloaderActive}
				isPortfolioIndividual={isPortfolioIndividual}
			/>

			<HighOrderComponent
				isFooter={!shouldShowPreloader}
				isMobileScreen={isMobileScreen}
				isHomePortfolio={isHomePortfolio}
			>
				{children}
			</HighOrderComponent>
		</>
	);
};

export default withDataContext(RootTemplate, ['preloader']);
